import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { FeatureFlagService } from '../../shared-services/feature-flag/feature-flag.service';
import { catchError, first, map } from 'rxjs/operators';

/**
 * It is very important to use with canActivate only! on NESTED routes. If used on top route,
 * most likely will prevent MSAL to finish initialization and will cause DEAD lock on feature flag resolver.
 */
export function featureFlagGuard(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
  const flagName = route.data?.featureFlag ?? '';
  const router = inject(Router);
  if (!flagName) {
    router.navigate(['not-found']);
    return of(false);
  }

  return inject(FeatureFlagService).getFlag(flagName).pipe(map((value) => {
    if (!value) {
      router.navigate(['not-found']);
      return false;
    }
    else {
      return true;
    }
  }));
}

/**
 * canMatch guard for route-level module switching based on a feature flag.
 * Waits for the LD client to bootstrap (waitUntilReady) before evaluating the flag,
 * using the anonymous user's value — safe to use before MSAL/session are established.
 * Defaults to false (fallback route) if the flag is off, missing, or LD is unreachable.
 */
export function featureFlagCanMatch(route: Route, segments: UrlSegment[]): Observable<boolean> {
  const flagName = route.data?.featureFlag ?? '';
  if (!flagName) return of(false);

  const featureFlagService = inject(FeatureFlagService);
  if (!featureFlagService.client) return of(false);

  return from(featureFlagService.client.waitUntilReady()).pipe(
    map(() => !!featureFlagService.client.variation(flagName, false)),
    catchError(() => of(false)),
    first()
  );
}